<script setup>
import {computed} from 'vue'

const props = defineProps({
    previousConference: {
        type: Object,
        required: true,
    },
})

/**
 * Input example: previousConference = { poster_overview_file: "/media/posters.pdf", abstracts_file: null }
 * Output example: [{ key: "poster-overview", title: "Poster Overview", action: "Download Poster Overview" }]
 */
const downloads = computed(() => {
    const items = []

    if (props.previousConference.poster_overview_file) {
        items.push({
            key: 'poster-overview',
            title: 'Poster Overview',
            description: 'Complete list of posters presented during the conference.',
            href: props.previousConference.poster_overview_file,
            action: 'Download Poster Overview',
        })
    }

    if (props.previousConference.abstracts_file) {
        items.push({
            key: 'abstracts',
            title: 'Abstract Book',
            description: 'Full conference agenda together with presentation abstracts.',
            href: props.previousConference.abstracts_file,
            action: 'Download Abstract Book',
        })
    }

    return items
})

/**
 * Input example: previousConference = { downloads_section_text: "Download the 2024 abstract book." }
 * Output example: "Download the 2024 abstract book."
 */
const downloadsSectionText = computed(() => {
    const customText = props.previousConference.downloads_section_text?.trim()

    if (customText) {
        return customText
    }

    return `Explore the scientific programme and presentation abstracts from ${props.previousConference.date}.`
})

/**
 * Input example: href = "/media/abstracts/PersonalizedHealth2026.pdf"
 * Output example: "PersonalizedHealth2026.pdf"
 */
function getDownloadFileName(href) {
    if (!href) {
        return 'download'
    }

    const hrefParts = href.split('/')
    return hrefParts[hrefParts.length - 1] || 'download'
}
</script>

<template>
    <section v-if="downloads.length > 0">
        <div
            class="flex flex-col gap-6 rounded-md border border-slate-200 bg-gradient-to-r from-slate-50 via-white to-slate-50 px-6 py-5 shadow-sm lg:flex-row lg:items-center lg:justify-between">
            <div class="flex items-start gap-4">
                <div
                    class="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-teal-50 text-teal-700 ring-1 ring-teal-100">
                    <svg
                        class="h-7 w-7"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.8"
                        stroke-linecap="round"
                        stroke-linejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
                        <path d="M14 2v6h6" />
                        <path d="M12 12v6" />
                        <path d="m9 15 3 3 3-3" />
                    </svg>
                </div>

                <div>
                    <h2 class="font-heading text-xl font-semibold text-slate-900">
                        Conference Program &amp; Abstract Book
                    </h2>

                    <p class="mt-2 max-w-2xl whitespace-pre-line text-sm leading-6 text-slate-600">
                        {{ downloadsSectionText }}
                    </p>

                    <div class="mt-4 space-y-2 text-sm leading-6 text-slate-600">
                        <p v-for="download in downloads" :key="download.key">
                            <span class="font-semibold text-slate-900">{{ download.title }}:</span>
                            {{ download.description }}
                        </p>
                    </div>
                </div>
            </div>

            <div class="flex flex-col gap-3 sm:flex-row sm:flex-wrap lg:justify-end">
                <a
                    v-for="download in downloads"
                    :key="download.key"
                    :href="download.href"
                    :download="getDownloadFileName(download.href)"
                    class="inline-flex items-center justify-center rounded-md bg-teal-700 px-5 py-3 text-sm font-semibold text-white transition hover:bg-teal-800">
                    {{ download.action }}
                </a>
            </div>
        </div>
    </section>
</template>

<style scoped></style>
